
import { useEffect, useState, useMemo } from "react";
import {
    FaCircleCheck,
    FaCircleInfo,
    FaCircleXmark,
    FaUser,
    FaMoneyBill,
    FaQrcode,
    FaBuildingColumns,
} from "react-icons/fa6";
import { FaCalendarAlt } from "react-icons/fa";

import { formatRupiah } from "../../../../utilities/formatRupiah";
import { ModalWraper } from "../../../../components/modal/wraper";
import { InvoiceTransactionService } from "../../../../services/InvoiceTransactionService";
import { SelectBankAccount } from "../../../../components/forms/selects/SelectBankAccount";
import { InputDefault } from "../../../../components/forms/InputDefault";


// ============================================================
// PAYMENT METHODS
// ============================================================

const PAYMENT_METHODS = [
    {
        value: "cash",
        label: "Cash",
        icon: FaMoneyBill,
        desc: "Bayar tunai",
    },
    {
        value: "qris",
        label: "QRIS",
        icon: FaQrcode,
        desc: "Scan QR code",
    },
    {
        value: "transfer",
        label: "Transfer",
        icon: FaBuildingColumns,
        desc: "Transfer bank",
    },
];


// ============================================================
// PAYMENT BADGE
// ============================================================

const getPaymentBadge = (status) => {
    if (!status) {
        return {
            color: "#94a3b8",
            bg: "rgba(148, 163, 184, 0.1)",
            border: "rgba(148, 163, 184, 0.2)",
            label: "Unknown",
            icon: FaCircleInfo,
        };
    }

    const s = status
        .toLowerCase()
        .replace(/[_\s-]/g, "");

    if (s === "belumbayar") {
        return {
            color: "#ef4444",
            bg: "rgba(239,68,68,0.1)",
            border: "rgba(239,68,68,0.2)",
            label: "Belum Dibayar",
            icon: FaCircleXmark,
        };
    }

    if (
        s === "dibayarsebagian" ||
        s === "sebagian"
    ) {
        return {
            color: "#f59e0b",
            bg: "rgba(245,158,11,0.1)",
            border: "rgba(245,158,11,0.2)",
            label: "Sebagian",
            icon: FaCircleInfo,
        };
    }

    if (s === "lunas") {
        return {
            color: "#10b981",
            bg: "rgba(16,185,129,0.1)",
            border: "rgba(16,185,129,0.2)",
            label: "Lunas",
            icon: FaCircleCheck,
        };
    }

    return {
        color: "#94a3b8",
        bg: "rgba(148,163,184,0.1)",
        border: "rgba(148,163,184,0.2)",
        label: status,
        icon: FaCircleInfo,
    };
};


// ============================================================
// COMPONENT
// ============================================================

export const ModalInvoicePayment = ({
    refresh,
    openModal,
    setOpenModal,
    data,
}) => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // ========================================================
    // FORM STATE
    // ========================================================

    const [formData, setFormData] = useState({
        nominal: 0,
        discount: 0,
        ppn: 0,
        nominal_bayar: 0,
        payment_method: "cash",
        bank_account_id: null,
    });


    // ========================================================
    // RESET FORM
    // ========================================================

    useEffect(() => {
        if (data && openModal) {
            setFormData({
                nominal: Number(data.nominal) || 0,
                discount: 0,
                ppn: 0,
                nominal_bayar: Number(data.nominal) || 0,
                payment_method: "cash",
                bank_account_id: null,
            });

            setErrors({});
        }
    }, [data, openModal]);


    // ========================================================
    // CALCULATION
    // ========================================================

    const calc = useMemo(() => {
        const subtotal = Number(formData.nominal) || 0;

        const discountVal =
            Number(formData.discount) || 0;

        const ppnPercent =
            Number(formData.ppn) || 0;

        const afterDiscount = Math.max(
            0,
            subtotal - discountVal
        );

        const ppnAmount =
            afterDiscount * (ppnPercent / 100);

        const grandTotal =
            afterDiscount + ppnAmount;

        return {
            subtotal,
            discountVal,
            afterDiscount,
            ppnPercent,
            ppnAmount,
            grandTotal,
        };
    }, [
        formData.nominal,
        formData.discount,
        formData.ppn,
    ]);


    // ========================================================
    // PAYMENT DIFFERENCE
    // ========================================================

    const paymentDifference = useMemo(() => {
        const paid =
            Number(formData.nominal_bayar) || 0;

        return paid - calc.grandTotal;
    }, [
        formData.nominal_bayar,
        calc.grandTotal,
    ]);


    const isUnderpaid = paymentDifference < 0;

    const isOverpaid = paymentDifference > 0;

    const isExactPayment = paymentDifference === 0;


    // ========================================================
    // CLOSE MODAL
    // ========================================================

    const closeModal = () => {
        setOpenModal(false);

        setFormData({
            nominal: 0,
            discount: 0,
            ppn: 0,
            nominal_bayar: 0,
            payment_method: "cash",
            bank_account_id: null,
        });

        setErrors({});
    };


    // ========================================================
    // HANDLE CHANGE
    // ========================================================

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors((prev) => {
                const next = { ...prev };

                delete next[field];

                return next;
            });
        }

        // Hapus error terkait backend field
        if (field === "payment_method") {
            setErrors((prev) => {
                const next = { ...prev };

                delete next.payment_method;
                delete next.payment_method_name;

                return next;
            });
        }

        if (field === "nominal_bayar") {
            setErrors((prev) => {
                const next = { ...prev };

                delete next.nominal_bayar;
                delete next.nominal;

                return next;
            });
        }

        if (field === "bank_account_id") {
            setErrors((prev) => {
                const next = { ...prev };

                delete next.bank_account_id;

                return next;
            });
        }
    };


    // ========================================================
    // CHANGE PAYMENT METHOD
    // ========================================================

    const handlePaymentMethodChange = (method) => {
        setFormData((prev) => ({
            ...prev,

            payment_method: method,

            // Cash tidak membutuhkan akun bank
            bank_account_id:
                method === "cash"
                    ? null
                    : prev.bank_account_id,
        }));

        setErrors((prev) => {
            const next = { ...prev };

            delete next.payment_method;
            delete next.payment_method_name;
            delete next.bank_account_id;

            return next;
        });
    };


    // ========================================================
    // SUBMIT
    // ========================================================

    const handleSubmit = async (e) => {
        e.preventDefault();

        setErrors({});

        const errs = {};

        const paymentMethod =
            formData.payment_method;

        const paymentAmount =
            Number(formData.nominal_bayar) || 0;


        // ----------------------------------------------------
        // PAYMENT METHOD
        // ----------------------------------------------------

        if (!paymentMethod) {
            errs.payment_method =
                "Pilih metode pembayaran.";
        }


        // ----------------------------------------------------
        // BANK ACCOUNT
        // Transfer & QRIS wajib akun bank
        // ----------------------------------------------------

        if (
            ["transfer", "qris"].includes(
                paymentMethod
            ) &&
            !formData.bank_account_id
        ) {
            errs.bank_account_id =
                "Akun bank harus dipilih.";
        }


        // ----------------------------------------------------
        // PAYMENT AMOUNT
        // ----------------------------------------------------

        if (paymentAmount <= 0) {
            errs.nominal_bayar =
                "Nominal pembayaran harus lebih dari 0.";
        }


        // ----------------------------------------------------
        // PRICE
        // ----------------------------------------------------

        if (
            !formData.nominal ||
            Number(formData.nominal) <= 0
        ) {
            errs.price =
                "Harga dasar harus lebih dari 0.";
        }


        // ----------------------------------------------------
        // DISCOUNT
        // ----------------------------------------------------

        if (Number(formData.discount) < 0) {
            errs.discount =
                "Diskon tidak boleh kurang dari 0.";
        }


        // ----------------------------------------------------
        // PPN
        // ----------------------------------------------------

        if (Number(formData.ppn) < 0) {
            errs.ppn =
                "PPN tidak boleh kurang dari 0.";
        }


        // ----------------------------------------------------
        // VALIDATION RESULT
        // ----------------------------------------------------

        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }


        // ====================================================
        // PAYLOAD
        // ====================================================

        const payload = {
            invoice_id: data.id,

            payment_method_name:
                formData.payment_method,

            price:
                Number(formData.nominal),

            discount:
                Number(formData.discount) || 0,

            ppn:
                Number(formData.ppn) || 0,

            nominal:
                paymentAmount,

            bank_account_id:
                ["transfer", "qris"].includes(
                    formData.payment_method
                )
                    ? formData.bank_account_id
                    : null,
        };


        setLoading(true);

        try {
            const res =
                await InvoiceTransactionService.create({
                    data: payload,
                });


            // =================================================
            // SUCCESS
            // =================================================

            if (
                res.data?.status === "success" ||
                res.status === 201 ||
                res.status === 200
            ) {
                if (refresh) {
                    refresh();
                }

                closeModal();

                return;
            }


            // =================================================
            // API ERROR
            // =================================================

            const message =
                res.data?.message_v2 ||
                res.data?.message ||
                "Gagal memproses pembayaran.";

            setErrors({
                general: message,
            });

        } catch (err) {
            console.error(
                "Payment Error:",
                err
            );


            // =================================================
            // VALIDATION ERROR 422
            // =================================================

            if (
                err.response?.status === 422 &&
                err.response?.data?.errors
            ) {
                const serverErrors =
                    err.response.data.errors;

                const formattedErrors = {};

                Object.keys(serverErrors).forEach(
                    (key) => {
                        formattedErrors[key] =
                            Array.isArray(
                                serverErrors[key]
                            )
                                ? serverErrors[key][0]
                                : serverErrors[key];
                    }
                );

                setErrors(
                    formattedErrors
                );

                return;
            }


            // =================================================
            // OTHER ERROR
            // =================================================

            const errorMessage =
                err.response?.data?.message_v2 ||
                err.response?.data?.message ||
                err.response?.data?.error ||
                "Terjadi kesalahan pada server.";

            setErrors({
                general: errorMessage,
            });

        } finally {
            setLoading(false);
        }
    };


    // ========================================================
    // NO DATA
    // ========================================================

    if (!data) {
        return null;
    }


    // ========================================================
    // BADGE
    // ========================================================

    const badge =
        getPaymentBadge(data.status);

    const BadgeIcon =
        badge.icon;


    // ========================================================
    // RENDER
    // ========================================================

    return (
        <ModalWraper
            isOpen={openModal}
            setIsOpen={setOpenModal}
            title="Pembayaran Invoice"
            subtitle={`Invoice #${data.invoice_number}`}
            icon={FaMoneyBill}
            maxWidth="max-w-4xl"
        >

            <form
                onSubmit={handleSubmit}
                className="space-y-6"
            >

                {/* =====================================================
                    GENERAL ERROR
                ====================================================== */}

                {errors.general && (
                    <div className="rounded-xl p-4 flex items-center gap-3 bg-red-500/10 border border-red-500/20">

                        <FaCircleXmark className="text-red-400 flex-shrink-0" />

                        <p className="text-sm text-red-400">
                            {errors.general}
                        </p>

                    </div>
                )}


                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                    {/* =================================================
                        LEFT COLUMN
                    ================================================== */}

                    <div className="lg:col-span-7 space-y-5">

                        {/* =============================================
                            CUSTOMER
                        ============================================== */}

                        <div className="rounded-xl p-4 bg-slate-900/50 border border-slate-800 flex items-center gap-4">

                            <div className="w-12 h-12 rounded-full bg-cyan-500/10 flex items-center justify-center flex-shrink-0 border border-cyan-500/20">

                                <FaUser className="text-cyan-400" />

                            </div>

                            <div className="flex-1 min-w-0">

                                <p className="text-sm font-bold text-slate-200 truncate">
                                    {data.pppoe_user?.name ||
                                        "Pelanggan"}
                                </p>

                                <p className="text-xs text-slate-500 font-mono mt-0.5">
                                    ID Internet:{" "}
                                    {data.pppoe_user
                                        ?.internet_number ||
                                        "-"}
                                </p>

                            </div>

                            <div className="text-right">

                                <span
                                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider whitespace-nowrap border"
                                    style={{
                                        color: badge.color,
                                        background:
                                            badge.bg,
                                        borderColor:
                                            badge.border,
                                    }}
                                >
                                    <BadgeIcon className="text-[9px]" />

                                    {badge.label}
                                </span>

                            </div>

                        </div>


                        {/* =============================================
                            PERIOD
                        ============================================== */}

                        <div className="grid grid-cols-2 gap-4">

                            <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-800">

                                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">
                                    Periode
                                </p>

                                <div className="flex items-center gap-2 text-cyan-400 font-bold text-sm">

                                    <FaCalendarAlt />

                                    {data.periode ||
                                        data.start_periode ||
                                        "-"}

                                </div>

                            </div>


                            <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-800">

                                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">
                                    Jatuh Tempo
                                </p>

                                <p className="text-slate-200 font-bold text-sm">
                                    {data.due_date ||
                                        "-"}
                                </p>

                            </div>

                        </div>


                        {/* =============================================
                            BILL DETAIL
                        ============================================== */}

                        <div className="rounded-xl p-5 bg-slate-900/40 border border-slate-800 space-y-4">

                            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-800 pb-2">
                                Rincian Tagihan
                            </h3>


                            {/* PRICE */}

                            <InputDefault
                                id="price"
                                label="Harga Dasar"
                                placeholder="Masukkan harga dasar..."
                                value={
                                    formData.nominal
                                }
                                onChange={(e) =>
                                    handleChange(
                                        "nominal",
                                        e.target.value
                                    )
                                }
                                error={
                                    errors.price
                                }
                            />


                            <div className="grid grid-cols-2 gap-4">

                                {/* DISCOUNT */}

                                <InputDefault
                                    id="discount"
                                    label="Diskon (Rp)"
                                    placeholder="Masukkan diskon..."
                                    inputMode="numeric"
                                    value={
                                        formData.discount
                                    }
                                    onChange={(e) =>
                                        handleChange(
                                            "discount",
                                            e.target.value
                                        )
                                    }
                                    error={
                                        errors.discount
                                    }
                                />


                                {/* PPN */}

                                <InputDefault
                                    id="ppn"
                                    label="PPN (%)"
                                    placeholder="Masukkan PPN..."
                                    inputMode="numeric"
                                    value={
                                        formData.ppn
                                    }
                                    onChange={(e) =>
                                        handleChange(
                                            "ppn",
                                            e.target.value
                                        )
                                    }
                                    error={
                                        errors.ppn
                                    }
                                />

                            </div>


                            {/* CALCULATION */}

                            <div className="bg-slate-950/50 rounded-lg p-3 space-y-2 border border-slate-800/50 mt-2">

                                <div className="flex justify-between text-xs text-slate-400">

                                    <span>
                                        Subtotal
                                    </span>

                                    <span className="text-slate-200">
                                        Rp{" "}
                                        {formatRupiah(
                                            calc.subtotal
                                        )}
                                    </span>

                                </div>


                                {calc.discountVal > 0 && (
                                    <div className="flex justify-between text-xs text-red-400">

                                        <span>
                                            Diskon
                                        </span>

                                        <span>
                                            - Rp{" "}
                                            {formatRupiah(
                                                calc.discountVal
                                            )}
                                        </span>

                                    </div>
                                )}


                                {calc.ppnAmount > 0 && (
                                    <div className="flex justify-between text-xs text-cyan-400">

                                        <span>
                                            PPN (
                                            {
                                                calc.ppnPercent
                                            }
                                            %)
                                        </span>

                                        <span>
                                            + Rp{" "}
                                            {formatRupiah(
                                                calc.ppnAmount
                                            )}
                                        </span>

                                    </div>
                                )}


                                <div className="border-t border-slate-800 my-1" />


                                <div className="flex justify-between items-center">

                                    <span className="text-xs font-bold text-slate-300 uppercase">
                                        Total Tagihan
                                    </span>

                                    <span className="text-lg font-extrabold text-cyan-400">
                                        Rp{" "}
                                        {formatRupiah(
                                            calc.grandTotal
                                        )}
                                    </span>

                                </div>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        RIGHT COLUMN
                    ================================================== */}

                    <div className="lg:col-span-5 space-y-5">

                        <div className="rounded-xl p-5 bg-slate-900/40 border border-slate-800 space-y-5 h-full flex flex-col justify-between">

                            <div className="grid gap-3">

                                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
                                    Metode Pembayaran
                                </h3>


                                {/* =====================================
                                    PAYMENT METHOD
                                ====================================== */}

                                <div className="grid grid-cols-3 gap-2">

                                    {PAYMENT_METHODS.map(
                                        (method) => {

                                            const isSelected =
                                                formData.payment_method ===
                                                method.value;

                                            const Icon =
                                                method.icon;

                                            return (
                                                <button
                                                    key={
                                                        method.value
                                                    }
                                                    type="button"
                                                    onClick={() =>
                                                        handlePaymentMethodChange(
                                                            method.value
                                                        )
                                                    }
                                                    className={`flex flex-col items-center justify-center gap-2 rounded-xl p-3 transition-all border ${
                                                        isSelected
                                                            ? "bg-cyan-500/10 border-cyan-500/50 text-cyan-400"
                                                            : "bg-slate-900 border-slate-700 text-slate-500 hover:bg-slate-800"
                                                    }`}
                                                >

                                                    <Icon className="text-lg" />

                                                    <span className="text-[10px] font-bold uppercase">
                                                        {
                                                            method.label
                                                        }
                                                    </span>

                                                </button>
                                            );
                                        }
                                    )}

                                </div>


                                {/* =====================================
                                    BANK ACCOUNT
                                ====================================== */}

                                {[
                                    "transfer",
                                    "qris",
                                ].includes(
                                    formData.payment_method
                                ) && (
                                    <div className="space-y-1">

                                        <SelectBankAccount
                                            label="Akun Bank"
                                            value={
                                                formData.bank_account_id
                                            }
                                            setValue={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    bank_account_id: e.id,
                                                }))
                                            }
                                            searchable={
                                                true
                                            }
                                            disabled={
                                                false
                                            }
                                        />

                                        {errors.bank_account_id && (
                                            <p className="text-[10px] text-red-400">
                                                {
                                                    errors.bank_account_id
                                                }
                                            </p>
                                        )}

                                    </div>
                                )}


                                {/* PAYMENT METHOD ERROR */}

                                {(
                                    errors.payment_method ||
                                    errors.payment_method_name
                                ) && (
                                    <p className="text-[10px] text-red-400">
                                        {errors.payment_method ||
                                            errors.payment_method_name}
                                    </p>
                                )}


                                {/* =====================================
                                    PAYMENT NOMINAL
                                ====================================== */}

                                <div className="mb-2">

                                    <label className="block text-xs text-slate-500 mb-1.5">
                                        Nominal Dibayar
                                    </label>

                                    <div className="relative">

                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-bold">
                                            Rp
                                        </span>

                                        <input
                                            type="number"
                                            min="0"
                                            value={
                                                formData.nominal_bayar
                                            }
                                            onChange={(e) =>
                                                handleChange(
                                                    "nominal_bayar",
                                                    e.target.value
                                                )
                                            }
                                            className={`w-full bg-slate-900 border rounded-xl pl-10 pr-16 py-3 text-lg font-bold text-slate-200 text-right focus:border-cyan-500 focus:outline-none transition ${
                                                errors.nominal ||
                                                errors.nominal_bayar
                                                    ? "border-red-500/50"
                                                    : "border-slate-700"
                                            }`}
                                        />


                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleChange(
                                                    "nominal_bayar",
                                                    calc.grandTotal
                                                )
                                            }
                                            className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded text-[9px] font-bold uppercase bg-slate-800 text-cyan-500 border border-slate-700 hover:bg-slate-700 transition"
                                        >
                                            Pas
                                        </button>

                                    </div>


                                    {(errors.nominal ||
                                        errors.nominal_bayar) && (
                                        <p className="text-[10px] text-red-400 mt-1">
                                            {errors.nominal ||
                                                errors.nominal_bayar}
                                        </p>
                                    )}

                                </div>


                                {/* =====================================
                                    PAYMENT SUMMARY
                                ====================================== */}

                                {formData.nominal_bayar > 0 && (
                                    <div
                                        className={`rounded-xl p-4 border ${
                                            isUnderpaid
                                                ? "bg-red-500/5 border-red-500/20"
                                                : isOverpaid
                                                ? "bg-emerald-500/5 border-emerald-500/20"
                                                : "bg-cyan-500/5 border-cyan-500/20"
                                        }`}
                                    >

                                        {/* TOTAL BILL */}

                                        <div className="flex justify-between text-xs text-slate-400 mb-1">

                                            <span>
                                                Total Tagihan
                                            </span>

                                            <span>
                                                Rp{" "}
                                                {formatRupiah(
                                                    calc.grandTotal
                                                )}
                                            </span>

                                        </div>


                                        {/* PAYMENT */}

                                        <div className="flex justify-between text-xs text-slate-400 mb-2">

                                            <span>
                                                Uang Diterima
                                            </span>

                                            <span className="text-cyan-400 font-bold">
                                                Rp{" "}
                                                {formatRupiah(
                                                    formData.nominal_bayar
                                                )}
                                            </span>

                                        </div>


                                        <div className="border-t border-slate-700/50 my-2" />


                                        {/* DIFFERENCE */}

                                        <div className="flex justify-between items-center">

                                            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">

                                                {isUnderpaid
                                                    ? "Kurang Bayar"
                                                    : isOverpaid
                                                    ? "Kembalian"
                                                    : "Status"}

                                            </span>


                                            <span
                                                className={`text-xl font-extrabold ${
                                                    isUnderpaid
                                                        ? "text-red-400"
                                                        : isOverpaid
                                                        ? "text-emerald-400"
                                                        : "text-cyan-400"
                                                }`}
                                            >

                                                {isUnderpaid
                                                    ? "-"
                                                    : ""}

                                                Rp{" "}
                                                {formatRupiah(
                                                    Math.abs(
                                                        paymentDifference
                                                    )
                                                )}

                                            </span>

                                        </div>


                                        {/* EXACT PAYMENT */}

                                        {isExactPayment && (
                                            <div className="flex items-center justify-end gap-1.5 mt-2 text-[10px] font-bold text-cyan-400">

                                                <FaCircleCheck />

                                                Pembayaran pas

                                            </div>
                                        )}

                                    </div>
                                )}

                            </div>


                            {/* =========================================
                                SUBMIT
                            ========================================== */}

                            <div className="pt-2 mt-auto">

                                <button
                                    type="submit"
                                    disabled={
                                        loading ||
                                        Number(
                                            formData.nominal_bayar
                                        ) <= 0
                                    }
                                    className={`w-full py-3.5 rounded-xl font-bold text-sm uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/10 ${
                                        loading ||
                                        Number(
                                            formData.nominal_bayar
                                        ) <= 0
                                            ? "bg-slate-800 text-slate-500 cursor-not-allowed"
                                            : "bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white shadow-cyan-500/20"
                                    }`}
                                >

                                    {loading ? (
                                        <>
                                            Memproses...
                                        </>
                                    ) : (
                                        <>
                                            <FaCircleCheck />

                                            Proses Pembayaran
                                        </>
                                    )}

                                </button>


                                <p className="text-[10px] text-center text-slate-600 mt-3">
                                    Pastikan nominal dan metode pembayaran
                                    sudah sesuai sebelum memproses.
                                </p>

                            </div>

                        </div>

                    </div>

                </div>

            </form>

        </ModalWraper>
    );
};
